/**
 * SeasonalityChart - Gráfico de Sazonalidade
 * Mostra padrão de produção BR vs tendência de preço ao longo do ano
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Area, ComposedChart, Line } from 'recharts';
import { Calendar, Sun, Leaf } from 'lucide-react';

const SeasonalityChart = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSeasonality();
  }, []);

  const fetchSeasonality = async () => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${API_URL}/api/market/seasonality`);
      if (res.ok) {
        const json = await res.json();
        setData(json);
      }
    } catch (err) {
      console.error('Erro ao carregar sazonalidade:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading || !data) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto" />
        </CardContent>
      </Card>
    );
  }

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const monthData = data.months.find(m => m.month === label);
      return (
        <div className="bg-white p-3 border rounded-lg shadow-lg text-sm max-w-xs">
          <p className="font-bold text-gray-800 mb-1">{label}</p>
          {payload.map((entry, idx) => (
            <p key={idx} style={{ color: entry.color }}>
              {entry.name}: {entry.value}%
            </p>
          ))}
          {monthData && (
            <p className="text-xs text-gray-500 mt-1 border-t pt-1">{monthData.notas}</p>
          )}
        </div>
      );
    }
    return null;
  };

  return (
    <Card data-testid="seasonality-chart">
      <CardHeader className="pb-2">
        <CardTitle className="text-gray-800 flex items-center gap-2">
          <Calendar className="w-5 h-5 text-green-600" />
          Sazonalidade do Mercado
        </CardTitle>
        <p className="text-sm text-gray-500">
          Padrão anual de produção e tendência de preço
        </p>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={300}>
          <ComposedChart data={data.months} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="month" tick={{ fontSize: 12 }} />
            <YAxis 
              tick={{ fontSize: 11 }} 
              tickFormatter={(v) => `${v}%`}
              domain={[0, 100]}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            
            <Bar
              dataKey="producao_br"
              name="Produção BR"
              fill="#86efac"
              radius={[4, 4, 0, 0]}
              opacity={0.8}
            />
            <Line
              type="monotone"
              dataKey="preco_tendencia"
              name="Tendência Preço"
              stroke="#f59e0b"
              strokeWidth={3}
              dot={{ r: 4, fill: '#f59e0b' }}
            />
          </ComposedChart>
        </ResponsiveContainer>

        {/* Períodos chave */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
          <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
            <div className="flex items-center gap-2 mb-1">
              <Sun className="w-4 h-4 text-amber-600" />
              <h4 className="font-bold text-amber-800 text-sm">Preços historicamente mais altos (Fev-Abr)</h4>
            </div>
            <p className="text-xs text-gray-600">{data.explanation.melhor_momento_venda}</p>
          </div>
          <div className="p-3 bg-red-50 rounded-lg border border-red-200">
            <div className="flex items-center gap-2 mb-1">
              <Leaf className="w-4 h-4 text-red-600" />
              <h4 className="font-bold text-red-800 text-sm">Preços historicamente mais baixos (Set-Out)</h4>
            </div>
            <p className="text-xs text-gray-600">{data.explanation.pior_momento_venda}</p>
          </div>
        </div>

        {/* Explicação do wintering e safra */}
        <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
          <p className="text-xs text-gray-700">
            <strong>Wintering (Fev-Abr):</strong> {data.explanation.wintering}
          </p>
          <p className="text-xs text-gray-700 mt-1">
            <strong>Safra BR (Ago-Nov):</strong> {data.explanation.safra_br}
          </p>
        </div>

        <p className="text-xs text-gray-400 mt-3 text-center">{data.note}</p>
      </CardContent>
    </Card>
  );
};

export default SeasonalityChart;
